// ── what a tap on a link row asks the screen to do ──────────────────────────
//
// A row in the chat is a place. Most places the shell glides to (an
// `innernet:netti-nav` with the path), but three kinds are not a path the shell
// can resolve, and sending them there lands the person on the wrong page:
//
//   a tab of a panel   `/settings?view=data` — the tree has no node for a query,
//                      so the shell would fall back to the dashboard. The tab is
//                      opened by the UI bridge (panel_view), which leaves the view
//                      for the panel and carries the shell to the panel itself.
//   a connection       `/connections/<tool>` — the tool's own guided window
//                      (open_connect), the one netti's guides open.
//   the open web       `https://…` — a new browser tab (open_web), https only.
//
// Pure: no window, no fetch. The chat rows ask this first and, when it says null,
// navigate as before. The bridge (components/netti/NettiUiBridge) performs what it
// returns, and checks every value again before it acts.

import type { NettiUi } from './tools/types'

/** the panels that take a tab, and the tabs they really have (the panels read exactly these ids) */
export const PANEL_VIEW_IDS: Readonly<Record<string, readonly string[]>> = {
  settings: ['account', 'app', 'chats', 'security', 'data'],
  connections: ['tools', 'apps', 'agents', 'mcp', 'npx', 'api', 'extensions'],
  memory: ['health', 'import'],
  // the netti space's "on your mac": netti's own settings, which exist only inside the Mac app for
  // someone who can see netti on the desk; and "identity", how every ai talks, answers and behaves
  // for whoever can see agent identity. The space ignores either id where it does not have that
  // view, so a link to it lands on the space's first view and never on an empty room.
  netti: ['mac', 'identity'],
}

/** an https address with no credentials in it: the only thing the web rail ever opens */
export function safeWebUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 600) return null
  try {
    const u = new URL(raw)
    if (u.protocol !== 'https:' || u.username || u.password || !u.hostname) return null
    return u.href
  } catch {
    return null
  }
}

/** `/settings?view=data` → { panel: 'settings', view: 'data' }, only for a tab the panel has */
export function panelViewOf(path: string): { panel: string; view: string } | null {
  const m = /^\/(settings|connections|memory|netti)\?(?:[^#]*&)?view=([a-z]+)(?:&[^#]*)?(?:#.*)?$/.exec(path)
  if (!m) return null
  return PANEL_VIEW_IDS[m[1]]?.includes(m[2]) ? { panel: m[1], view: m[2] } : null
}

/**
 * The directive a link asks of the screen, or null when the shell can simply go
 * to the path. `kind` is the link's own kind (a web link is the only place an
 * address outside the site is allowed).
 */
export function uiForPath(path: string, kind?: string): NettiUi | null {
  if (kind === 'web' || /^https:\/\//i.test(path)) {
    const url = safeWebUrl(path)
    return url ? { open_web: url } : null
  }
  const tab = panelViewOf(path)
  if (tab) return { panel_view: { panel: tab.panel as 'settings' | 'connections' | 'memory' | 'netti', view: tab.view } }
  const conn = /^\/connections\/([A-Za-z0-9_.%-]{1,64})\/?$/.exec(path)
  if (conn) {
    try {
      return { open_connect: { tool: decodeURIComponent(conn[1]) } }
    } catch {
      return null
    }
  }
  return null
}
